import React, { useState, useEffect } from 'react';
import './css/admin-dashboard.css';
import { authAPI, contactAPI, consultationAPI, languagesAPI, dashboardAPI, authUtils } from '../services/apiService';
import { useLanguage } from '../context/LanguageContext';

const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [contactMessages, setContactMessages] = useState([]);
  const [consultationRequests, setConsultationRequests] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const { refreshLanguages } = useLanguage();

  // Login form state
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  // Pagination state
  const [contactPagination, setContactPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });

  const [consultationPagination, setConsultationPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });

  // Check if user is already logged in
  useEffect(() => {
    if (authUtils.isAuthenticated()) {
      setIsLoggedIn(true);
      setUser(authUtils.getUser());
      fetchDashboardData();
    }
  }, []);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    if (activeTab === 'dashboard') {
      await fetchStats();
    } else if (activeTab === 'contacts') {
      await fetchContactMessages();
    } else if (activeTab === 'consultations') {
      await fetchConsultationRequests();
    } else if (activeTab === 'languages') {
      await fetchLanguages();
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchDashboardData();
    }
  }, [activeTab, isLoggedIn]);

  // Authentication
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await authAPI.login(loginForm);
      
      authUtils.setToken(data.token);
      authUtils.setUser(data.user);
      setIsLoggedIn(true);
      setUser(data.user);
      setActiveTab('dashboard');
    } catch (error) {
      alert(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authUtils.logout();
    setIsLoggedIn(false);
    setUser(null);
  };

  // API calls
  const fetchStats = async () => {
    try {
      const data = await dashboardAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchContactMessages = async (page = 1) => {
    try {
      const data = await contactAPI.getAll(page);
      setContactMessages(data.messages);
      setContactPagination({
        page: data.page,
        totalPages: data.totalPages,
        total: data.total
      });
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    }
  };

  const fetchConsultationRequests = async (page = 1) => {
    try {
      const data = await consultationAPI.getAll(page);
      setConsultationRequests(data.requests);
      setConsultationPagination({
        page: data.page,
        totalPages: data.totalPages,
        total: data.total
      });
    } catch (error) {
      console.error('Error fetching consultation requests:', error);
    }
  };

  const fetchLanguages = async () => {
    try {
      const data = await languagesAPI.getAll();
      setLanguages(data);
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  };

  // Update status functions
  const updateContactStatus = async (id, status) => {
    try {
      await contactAPI.updateStatus(id, status);
      await fetchContactMessages(contactPagination.page);
    } catch (error) {
      console.error('Error updating contact status:', error);
    }
  };

  const updateConsultationStatus = async (id, status) => {
    try {
      await consultationAPI.updateStatus(id, status);
      await fetchConsultationRequests(consultationPagination.page);
    } catch (error) {
      console.error('Error updating consultation status:', error);
    }
  };

  const toggleLanguage = async (code) => {
    try {
      await languagesAPI.toggle(code);
      await fetchLanguages();
      await fetchStats();
      // Refresh the language context to update the frontend language list
      await refreshLanguages();
    } catch (error) {
      console.error('Error toggling language:', error);
    }
  };

  // Login form
  if (!isLoggedIn) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h2>Admin Dashboard Login</h2>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Username or Email:</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Password:</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                required
              />
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          <div className="login-info">
            <p><strong>Default credentials:</strong></p>
            <p>Username: admin</p>
            <p>Password: admin123</p>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>Etgan Admin Dashboard</h1>
        <div className="user-info">
          <span>Welcome, {user?.username}</span>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      <nav className="dashboard-nav">
        <button 
          className={activeTab === 'dashboard' ? 'active' : ''} 
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button 
          className={activeTab === 'contacts' ? 'active' : ''} 
          onClick={() => setActiveTab('contacts')}
        >
          Contact Messages
        </button>
        <button 
          className={activeTab === 'consultations' ? 'active' : ''} 
          onClick={() => setActiveTab('consultations')}
        >
          Consultation Requests
        </button>
        <button 
          className={activeTab === 'languages' ? 'active' : ''} 
          onClick={() => setActiveTab('languages')}
        >
          Language Management
        </button>
      </nav>

      <main className="dashboard-content">
        {activeTab === 'dashboard' && (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Contact Messages</h3>
              <p className="stat-number">{stats.totalContacts || 0}</p>
            </div>
            <div className="stat-card">
              <h3>New Messages</h3>
              <p className="stat-number">{stats.newContacts || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Total Consultations</h3>
              <p className="stat-number">{stats.totalConsultations || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Pending Consultations</h3>
              <p className="stat-number">{stats.pendingConsultations || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Active Languages</h3>
              <p className="stat-number">{stats.activeLanguages || 0}</p>
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="messages-section">
            <h2>Contact Messages</h2>
            <div className="messages-list">
              {contactMessages.map((message) => (
                <div key={message.id} className="message-card">
                  <div className="message-header">
                    <h4>{message.fullName}</h4>
                    <span className={`status ${message.status}`}>{message.status}</span>
                  </div>
                  <div className="message-details">
                    <p><strong>Email:</strong> {message.email}</p>
                    <p><strong>Phone:</strong> {message.phone || 'N/A'}</p>
                    <p><strong>Activity:</strong> {message.activity || 'N/A'}</p>
                    <p><strong>Message:</strong> {message.message}</p>
                    <p><strong>Date:</strong> {new Date(message.created_at).toLocaleString()}</p>
                  </div>
                  <div className="message-actions">
                    <select 
                      value={message.status} 
                      onChange={(e) => updateContactStatus(message.id, e.target.value)}
                    >
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="replied">Replied</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <div className="pagination">
              <button 
                disabled={contactPagination.page === 1}
                onClick={() => fetchContactMessages(contactPagination.page - 1)}
              >
                Previous
              </button>
              <span>Page {contactPagination.page} of {contactPagination.totalPages}</span>
              <button 
                disabled={contactPagination.page === contactPagination.totalPages}
                onClick={() => fetchContactMessages(contactPagination.page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {activeTab === 'consultations' && (
          <div className="consultations-section">
            <h2>Consultation Requests</h2>
            <div className="consultations-list">
              {consultationRequests.map((request) => (
                <div key={request.id} className="consultation-card">
                  <div className="consultation-header">
                    <h4>{request.firstName} {request.lastName}</h4>
                    <span className={`status ${request.status}`}>{request.status}</span>
                  </div>
                  <div className="consultation-details">
                    <p><strong>Email:</strong> {request.email}</p>
                    <p><strong>Phone:</strong> {request.phone || 'N/A'}</p>
                    <p><strong>Nationality:</strong> {request.nationality || 'N/A'}</p>
                    <p><strong>Country:</strong> {request.country || 'N/A'}</p>
                    <p><strong>Service:</strong> {request.service || 'N/A'}</p>
                    <p><strong>Date:</strong> {new Date(request.created_at).toLocaleString()}</p>
                  </div>
                  <div className="consultation-actions">
                    <select 
                      value={request.status} 
                      onChange={(e) => updateConsultationStatus(request.id, e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <div className="pagination">
              <button 
                disabled={consultationPagination.page === 1}
                onClick={() => fetchConsultationRequests(consultationPagination.page - 1)}
              >
                Previous
              </button>
              <span>Page {consultationPagination.page} of {consultationPagination.totalPages}</span>
              <button 
                disabled={consultationPagination.page === consultationPagination.totalPages}
                onClick={() => fetchConsultationRequests(consultationPagination.page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {activeTab === 'languages' && (
          <div className="languages-section">
            <h2>Language Management</h2>
            <div className="languages-list">
              {languages.map((language) => (
                <div key={language.id} className="language-card">
                  <div className="language-info">
                    <h4>{language.name}</h4>
                    <p>Code: {language.code}</p>
                  </div>
                  <div className="language-actions">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={language.is_active}
                        onChange={() => toggleLanguage(language.code)}
                      />
                      <span className="slider"></span>
                    </label>
                    <span className={`status ${language.is_active ? 'active' : 'inactive'}`}>
                      {language.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
